import _filter from "lodash/filter";
import _sortBy from "lodash/sortBy";
import { UserClaimTypes, UserManager } from "core";
import { MediaAsset, MediaAssetType } from "mediaModules/media/models";
import MediaService from "mediaModules/media/services/MediaService";
import React from "react";
import { ExpandableRow, IExpandableRowControl } from "component-library";
import { IExternalMedia, MediaType } from "shared-types";
import { getMediaViewerUrl } from "services/UrlBuilder";

import { IAccessibilityContent } from "pages/MainPage/MainPage";

import { EmptyData } from "components/common/EmptyData";

import { EditPanel } from "../EditPanel";

import { AudioClips } from "./components/AudioClips";
import { Showreels } from "./components/Showreels";
import { heapTrackingForMediaSection } from "app/helpers/heapTracking";
import "./MediaSection.scss";

export interface IMediaSectionContent extends IAccessibilityContent {
  mediaHeader: string;
  showreelsHeader: string;
  audioClipsHeader: string;
  addShowreelsLabel: string;
  addAudioClipsLabel: string;
  showreelsCountTemplate: string;
  audioclipsCountTemplate: string;
  openInNewTabLabel: string;
}

export interface IMediaSectionProps {
  content: IMediaSectionContent;
  mediaAssets: MediaAsset[];
  mediaObjectPromiseMap: Map<string, Promise<MediaAsset>>;
  isReadonly: boolean;
  editShowreels: () => void;
  editAudioClips: () => void;
  openPopup: (popupContent, popupProps) => void;
  closePopup: () => void;
  externalMediaItems?: IExternalMedia[];
  isFilmmakers?: boolean;
}

export interface IMediaSectionState {
  mediaAssets: MediaAsset[];
  processedShowreelsCount: number;
  processedAudioClipsCount: number;
}

export class MediaSection extends React.Component<
  IMediaSectionProps,
  IMediaSectionState
> {
  constructor(props: IMediaSectionProps) {
    super(props);

    this.state = {
      mediaAssets: props.mediaAssets,
      processedShowreelsCount: 0,
      processedAudioClipsCount: 0,
    };
  }

  public render() {
    const { content, isReadonly, externalMediaItems } = this.props;
    const { mediaAssets, processedShowreelsCount, processedAudioClipsCount } =
      this.state;
    const { showreels, audioClips } =
      MediaService.filterMediaAssets(mediaAssets);
    const externalShowreels = _sortBy(
      _filter(externalMediaItems, {
        mediaType: MediaType.Video,
      }),
      "sortOrder"
    );
    const externalAudio = _sortBy(
      _filter(externalMediaItems, {
        mediaType: MediaType.Audio,
      }),
      "sortOrder"
    );

    const audioClipsLength = audioClips.length || externalAudio.length;
    const isAudioVisible = (isReadonly && audioClipsLength > 0) || !isReadonly;
    const showreelLength = showreels.length || externalShowreels.length;
    const isShowreelVisible = (isReadonly && showreelLength > 0) || !isReadonly;

    const expandableRowControls = this.getExpandableRowControls(
      processedShowreelsCount || externalShowreels.length,
      processedAudioClipsCount || externalAudio.length,
      content
    );

    return (
      <ExpandableRow
        header={content.mediaHeader}
        headerTextSize="large"
        headerTextWeight="regular"
        controls={expandableRowControls}
        toggleAriaLabel={content.mediaHeader}
      >
        <div className="c-media-content-section g-content-area">
          {isShowreelVisible &&
            this.renderShowreels(
              showreels,
              externalShowreels,
              audioClipsLength
            )}
          {isAudioVisible &&
            this.renderAudios(audioClips, externalAudio, showreelLength)}
        </div>
      </ExpandableRow>
    );
  }

  public static getDerivedStateFromProps(
    props: IMediaSectionProps,
    state: IMediaSectionState
  ) {
    const newState: Partial<IMediaSectionState> = {};

    if (props.mediaAssets !== state.mediaAssets) {
      newState.mediaAssets = props.mediaAssets;
    }

    return newState;
  }

  private renderPlaceholder = (text: string) => (
    <EmptyData theme="light" text={text} />
  );

  private renderShowreels = (
    showreels: MediaAsset[],
    externalShowreels: IExternalMedia[],
    audioClipsLength: number
  ) => {
    const {
      content,
      editShowreels,
      openPopup,
      closePopup,
      mediaObjectPromiseMap,
      isReadonly,
      isFilmmakers,
    } = this.props;

    const showreelsLength = showreels.length || externalShowreels.length;

    const columnSize = this.getShowreelsColumnSize(
      showreelsLength,
      audioClipsLength
    );
    const additionalControls = this.getAdditionalControls(
      showreelsLength,
      MediaAssetType.Video,
      isFilmmakers
    );
    const profileFullName = UserManager.getClaimWithDefault(
      UserClaimTypes.profileFullName
    );

    return (
      <div
        className={`c-media-content-section__content-wrapper g-col-lg-${columnSize} g-col-md-${columnSize}`}
      >
        <div className="c-media-content-section__outer-bg">
          <div className="c-media-content-section__inner-bg">
            <EditPanel
              header={content.showreelsHeader}
              editFn={editShowreels}
              hideEdit={isReadonly}
              additionalControls={additionalControls}
              editLinkAltText={content.editLinkAltText}
            />
            <div className="c-media-content-section__media-list-container-showreels">
              {showreelsLength ? (
                <Showreels
                  mediaObjectPromiseMap={mediaObjectPromiseMap}
                  showreels={showreels}
                  externalShowreels={externalShowreels}
                  openPopup={openPopup}
                  closePopup={closePopup}
                  updateProcessedShowreelsCount={
                    this.updateProcessedShowreelsCount
                  }
                  titlePrefix={profileFullName}
                />
              ) : (
                this.renderPlaceholder(content.addShowreelsLabel)
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  private renderAudios = (
    audioClips: MediaAsset[],
    externalAudio: IExternalMedia[],
    showreelsLength: number
  ) => {
    const {
      content,
      editAudioClips,
      openPopup,
      closePopup,
      mediaObjectPromiseMap,
      isReadonly,
      isFilmmakers,
    } = this.props;

    const audioClipsLength = audioClips.length || externalAudio.length;

    const columnSize = this.getAudioClipsColumnSize(
      audioClipsLength,
      showreelsLength
    );
    const additionalControls = this.getAdditionalControls(
      audioClipsLength,
      MediaAssetType.Audio,
      isFilmmakers
    );

    return (
      <div
        className={`c-media-content-section__content-wrapper g-col-lg-${columnSize} g-col-md-${columnSize}`}
      >
        <div className="c-media-content-section__outer-bg">
          <div className="c-media-content-section__inner-bg">
            <EditPanel
              header={content.audioClipsHeader}
              editFn={editAudioClips}
              hideEdit={isReadonly}
              additionalControls={additionalControls}
              editLinkAltText={content.editLinkAltText}
            />
            <div className="c-media-content-section__media-list-container-audio-clips">
              {audioClipsLength ? (
                <AudioClips
                  mediaObjectPromiseMap={mediaObjectPromiseMap}
                  audioClips={audioClips}
                  externalAudio={externalAudio}
                  openPopup={openPopup}
                  closePopup={closePopup}
                  showreelsLength={showreelsLength}
                  updateProcessedAudioClipsCount={
                    this.updateProcessedAudioClipsCount
                  }
                />
              ) : (
                this.renderPlaceholder(content.addAudioClipsLabel)
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  private getAdditionalControls = (
    mediaLength: number,
    assetType: MediaAssetType,
    hide?: boolean
  ): React.ReactNode[] => {
    const { content, isReadonly } = this.props;
    const controls: React.ReactNode[] = [];
    const handleClick = () => {
      heapTrackingForMediaSection();

      this.openMediaViewer(assetType);
    };

    if (isReadonly && mediaLength > 0 && !hide) {
      controls.push(
        <div
          key={assetType}
          className="c-media-content-section__open-link"
          onClick={handleClick}
        >
          <div className="c-media-content-section__open-icon icon-open-in-new-tab" />
          <div className="c-media-content-section__open-label">
            {content.openInNewTabLabel}
          </div>
        </div>
      );
    }

    return controls;
  };

  private openMediaViewer = (assetType: MediaAssetType) => {
    const url = getMediaViewerUrl(assetType, UserManager.artistRef || "");
    window.open(url, "_blank");
  };

  private updateProcessedShowreelsCount = (count: number) =>
    this.setState({ processedShowreelsCount: count });

  private updateProcessedAudioClipsCount = (count: number) =>
    this.setState({ processedAudioClipsCount: count });

  private getShowreelsColumnSize = (showreelsLength, audioClipsLength) => {
    let columnSize = 6;

    if (this.props.isReadonly) {
      if (showreelsLength > 0 && audioClipsLength === 0) {
        columnSize = 12;
      } else if (showreelsLength > 1 && audioClipsLength > 0) {
        columnSize = 8;
      }
    }

    return columnSize;
  };

  private getAudioClipsColumnSize = (audioClipsLength, showreelsLength) => {
    let columnSize = 6;

    if (this.props.isReadonly) {
      if (audioClipsLength > 0 && showreelsLength === 0) {
        columnSize = 12;
      } else if (showreelsLength > 1 && audioClipsLength > 0) {
        columnSize = 4;
      }
    }

    return columnSize;
  };

  private getExpandableRowControls = (
    showreelsCount: number,
    audioclipsCount: number,
    content: IMediaSectionContent
  ) => {
    const result: IExpandableRowControl[] = [];

    if (showreelsCount > 0) {
      result.push({
        isInteractionDisabled: true,
        icon: "showreel",
        text: content.showreelsCountTemplate?.replace(
          "{count}",
          `${showreelsCount}`
        ),
      });
    }

    if (audioclipsCount > 0) {
      result.push({
        isInteractionDisabled: true,
        icon: "audio",
        text: content.audioclipsCountTemplate.replace(
          "{count}",
          `${audioclipsCount}`
        ),
      });
    }

    return result;
  };
}
